import { createSelector } from "reselect";

import NylasStatus from "@app/enum/NylasStatus";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { getDefaultInboxSelection } from "@app/util/nylas/buildFolderTree";

import { RootState } from "../rootReducer";

const isNylasConnectionFailed = (nylasInfo?: NylasInfo) =>
  !!nylasInfo &&
  [null, NylasStatus.INVALID, NylasStatus.STOPPED, NylasStatus.SYNC_ERROR].includes(
    nylasInfo.status ?? null
  );

const nylasState = (state: RootState) => state.nylas;
export const isInitializing = createSelector(nylasState, ({ initializing }) => initializing);
export const getNylasCalendarInfo = createSelector(
  nylasState,
  ({ nylasCalendarInfo }) => nylasCalendarInfo
);
export const getEmailNylasInfo = createSelector(nylasState, ({ nylasEmailInfo }) => nylasEmailInfo);

export const isEmailNylasConnected = createSelector(
  getEmailNylasInfo,
  (nylasEmailInfo) => !!nylasEmailInfo
);

export const isNylasEmailConnectionFailed = createSelector(getEmailNylasInfo, (nylasEmailInfo) =>
  isNylasConnectionFailed(nylasEmailInfo)
);

export const isNylasEmailConnectionActive = createSelector(
  isEmailNylasConnected,
  isNylasEmailConnectionFailed,
  (nylasConnected, nylasConnectionFailed) => nylasConnected && !nylasConnectionFailed
);

export const getCalendars = createSelector(nylasState, ({ calendars }) => calendars);
export const areCalendarsLoading = createSelector(
  nylasState,
  ({ calendarsLoading }) => calendarsLoading
);

export const isCreatingCalendarEventForDealClosingDate = createSelector(
  nylasState,
  ({ createCalendarEventForDealClosingDateLoading }) => createCalendarEventForDealClosingDateLoading
);

export const isNylasCreating = createSelector(nylasState, ({ creating }) => creating);

export const isNylasUpdating = createSelector(nylasState, ({ updating }) => updating);

export const isNylasDeleting = createSelector(nylasState, ({ deleting }) => deleting);

export const isNylasCalendarSetupFinished = createSelector(
  nylasState,
  ({ nylasCalendarInfo }) => !!nylasCalendarInfo?.calendarId
);

export const isNylasCalendarConnectionFailed = createSelector(
  getNylasCalendarInfo,
  (nylasCalendarInfo) => isNylasConnectionFailed(nylasCalendarInfo)
);

export const getEmailFolders = createSelector(nylasState, ({ emailFolders }) => emailFolders);

export const areEmailFoldersLoading = createSelector(
  nylasState,
  ({ emailFoldersLoading }) => emailFoldersLoading
);

export const didEmailFoldersFail = createSelector(
  nylasState,
  ({ emailFoldersError }) => emailFoldersError
);

// Derive the Inbox-only default selection once (memoized on emailFolders) rather than rebuilding the
// folder tree in the component on every render.
export const getDefaultEmailInboxSelection = createSelector(
  getEmailFolders,
  getDefaultInboxSelection
);
